import { useState, useEffect } from 'react';
import { fetchFieldOptions } from '../services/baserow/fields';
import { BASEROW_CONFIG } from '../services/baserow/config';

export interface Tag {
  id: number;
  value: string;
}

const CACHE_KEY = 'baserow_tags';
const CACHE_DURATION = 1000 * 60 * 5; // 5 minutes

interface CachedData {
  tags: Tag[];
  timestamp: number;
}

export function useTags() {
  const [tags, setTags] = useState<Tag[]>(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { tags, timestamp }: CachedData = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          return tags;
        }
      }
    } catch (e) {
      console.warn('Failed to load cached tags:', e);
    }
    return [];
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const loadTags = async () => {
      try {
        setLoading(true);
        setError(null);
        const options = await fetchFieldOptions(BASEROW_CONFIG.FIELDS.TAGS);
        
        if (mounted) {
          const newTags = options.map(({ id, value }) => ({ id, value }));
          setTags(newTags);
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            tags: newTags,
            timestamp: Date.now()
          }));
        }
      } catch (err) {
        if (mounted) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load tags';
          console.error('Tags loading error:', errorMessage);
          setError(errorMessage);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadTags();

    return () => {
      mounted = false;
    };
  }, []);

  const reload = () => {
    localStorage.removeItem(CACHE_KEY);
    setTags([]);
    setLoading(true);
  };

  return { tags, loading, error, reload };
}